// Operator console › Connections (CONNECTIONS; Andrei 2026-10-25: "configurable in the settings
// so we don't have to change the code every time"). The platform's service endpoints — Recall,
// the LiteLLM gateway, AiGentPanel and the Hermes overlay policy — stored in the control plane,
// each falling back to its environment variable when nothing is stored. Only URLs and address
// lists: secrets stay in the sealed keyring and are never on this page.
//
// Platform admins only: an agency admin holding a live operator grant on the platform's own
// (marked) company, i.e. HTVA's agency. Other agency users are 403; anyone else 404, as every
// operator route. A change is recorded as
// `service_endpoint.changed` in the platform company's log and a `cp.platform_audit` row.
import { z } from "zod";
import { ServiceEndpointKey } from "../enums";
import { Timestamp, Uuid } from "../primitives";
import { defineRoute } from "./route";

const base = "/v1/operator/service-endpoints";
const core = { feature: "core", tag: "operator", access: "operator" } as const;

/** Where the value in force comes from. */
export const ServiceEndpointSource = z.enum(["stored", "env", "unset"]);
export type ServiceEndpointSource = z.infer<typeof ServiceEndpointSource>;

export const ServiceEndpointView = z
  .object({
    key: ServiceEndpointKey,
    label: z.string(),
    /** The environment variable that is the default. */
    env_var: z.string(),
    /**
     * `url`: a base URL; `cidrs` / `addresses`: comma-separated IPv4 ranges / addresses; `model`:
     * a gateway model name.
     */
    kind: z.enum(["url", "cidrs", "addresses", "model"]),
    description: z.string(),
    /** The value in force (stored, else the environment's), or null when neither is set. */
    value: z.string().nullable(),
    source: ServiceEndpointSource,
    stored_value: z.string().nullable(),
    env_value: z.string().nullable(),
    /** Whether "Test connection" applies (a URL the API can reach without credentials). */
    testable: z.boolean(),
    /** Changing the host sends this service's sealed credential to the new host: confirm first. */
    confirm_host_change: z.boolean(),
    /** Where a change takes effect (this API within seconds, the worker on its own restart, …). */
    applies: z.string(),
    /** Why the value in force does not build into a working configuration (null: it does). */
    problem: z.string().nullable(),
    /**
     * A warning that does not turn the service off, e.g. the environment's fallback carries a
     * user name or password (redacted here) while a stored value is in force.
     */
    warning: z.string().nullable(),
    updated_at: Timestamp.nullable(),
    updated_by_user_id: Uuid.nullable(),
  })
  .meta({ id: "ServiceEndpointView" });
export type ServiceEndpointView = z.infer<typeof ServiceEndpointView>;

export const ServiceEndpointList = z
  .object({
    data: z.array(ServiceEndpointView),
    /** How long another API process may keep serving the previous value. */
    cache_seconds: z.int().min(0),
  })
  .meta({ id: "ServiceEndpointList" });

export const ServiceEndpointParams = z.object({ key: ServiceEndpointKey });

/**
 * `confirm_host`: the new host, typed back, when the change moves a credentialed service to
 * another host (Recall: every company's tenant lives on the old one). Without it such a change is
 * 409 `conflict` with `current` = `ServiceEndpointHostChange`.
 */
export const ServiceEndpointSet = z
  .strictObject({
    value: z.string().trim().min(1).max(2000),
    confirm_host: z.string().trim().min(1).max(255).optional(),
  })
  .meta({ id: "ServiceEndpointSet" });

export const ServiceEndpointReset = z
  .strictObject({ confirm_host: z.string().trim().min(1).max(255).optional() })
  .meta({ id: "ServiceEndpointReset" });

/** The 409 `current` of an unconfirmed host change. */
export const ServiceEndpointHostChange = z
  .object({
    from_host: z.string().nullable(),
    to_host: z.string().nullable(),
    /** Companies with a Recall tenant today (Recall only; null otherwise). */
    tenants: z.int().min(0).nullable(),
  })
  .meta({ id: "ServiceEndpointHostChange" });

/** A candidate to test before saving; omitted: the value in force. */
export const ServiceEndpointTest = z
  .strictObject({ value: z.string().trim().min(1).max(2000).optional() })
  .meta({ id: "ServiceEndpointTest" });

export const ServiceEndpointTestResult = z
  .object({
    ok: z.boolean(),
    /** The HTTP status answered (redirects are never followed), null when nothing answered. */
    status: z.int().nullable(),
    latency_ms: z.int().min(0).nullable(),
    detail: z.string(),
    tested_url: z.string().nullable(),
  })
  .meta({ id: "ServiceEndpointTestResult" });

export const serviceEndpointRoutes = {
  "operator.serviceEndpoints.list": defineRoute({
    ...core,
    id: "operator.serviceEndpoints.list",
    method: "get",
    path: base,
    summary: "The platform's service endpoints: the value in force and where it comes from",
    response: { status: 200, schema: ServiceEndpointList },
    errors: [],
    emits: [],
  }),
  "operator.serviceEndpoints.set": defineRoute({
    ...core,
    id: "operator.serviceEndpoints.set",
    method: "put",
    path: `${base}/{key}`,
    summary:
      "Store a service endpoint (validated; a host change of a credentialed service is confirmed)",
    params: ServiceEndpointParams,
    body: ServiceEndpointSet,
    response: { status: 200, schema: ServiceEndpointView },
    errors: ["conflict"],
    emits: ["service_endpoint.changed"],
  }),
  "operator.serviceEndpoints.reset": defineRoute({
    ...core,
    id: "operator.serviceEndpoints.reset",
    method: "post",
    path: `${base}/{key}/reset`,
    summary: "Clear the stored value: the environment variable applies again",
    params: ServiceEndpointParams,
    body: ServiceEndpointReset,
    response: { status: 200, schema: ServiceEndpointView },
    errors: ["conflict"],
    emits: ["service_endpoint.changed"],
  }),
  "operator.serviceEndpoints.test": defineRoute({
    ...core,
    id: "operator.serviceEndpoints.test",
    method: "post",
    path: `${base}/{key}/test`,
    summary: "A safe health check: no credential sent, redirects not followed, a short deadline",
    params: ServiceEndpointParams,
    body: ServiceEndpointTest,
    safe: true,
    response: { status: 200, schema: ServiceEndpointTestResult },
    errors: [],
    emits: [],
  }),
};
